import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Award,
  ChevronRight,
  Clock,
  Plus,
  ShieldAlert,
  User,
  X,
  Zap,
  Archive,
  Layers,
} from "lucide-react";
import {
  evaluationsCreateEvaluation,
  evaluationsArchiveEvaluation,
  feedbackBatchesAddEvaluationsToBatch,
} from "../gen";
import type {
  EvaluationResponse,
  ThreadScorecardResponse,
  BatchResponse,
} from "../gen/models";
import { useToast } from "../context/ToastContext";
import { HumanReviewForm } from "./evaluation/HumanReviewForm";
import { ScorecardSummaryView } from "./evaluation/ScorecardSummaryView";
import { ConfirmModal } from "./ConfirmModal";

interface EvaluationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  threadId: string;
  evaluation?: EvaluationResponse | null;
  evaluations?: EvaluationResponse[];
  scorecard?: ThreadScorecardResponse | null;
  messageId?: string | null;
  runId?: string | null;
  initialHumanReviewMode?: boolean;
  onReviewSubmitted?: () => void;
  activeBatch?: BatchResponse | null;
  activeBatches?: BatchResponse[];
  allBatches?: BatchResponse[];
  onRequireActiveBatch?: (callback?: (batchId: string) => void) => void;
  onOpenBatchAssignModal?: (evals?: EvaluationResponse[]) => void;
  onAssignDirectToActiveBatch?: (evalIds: string[]) => Promise<void>;
}

export function EvaluationDrawer({
  isOpen,
  onClose,
  threadId,
  evaluation,
  evaluations = [],
  scorecard,
  messageId,
  runId,
  initialHumanReviewMode = false,
  onReviewSubmitted,
  activeBatch,
  activeBatches = [],
  allBatches = [],
  onRequireActiveBatch,
  onOpenBatchAssignModal,
  onAssignDirectToActiveBatch,
}: EvaluationDrawerProps) {
  const { toast } = useToast();
  const [isHumanMode, setIsHumanMode] = useState(initialHumanReviewMode);
  const [selectedEval, setSelectedEval] = useState<EvaluationResponse | null>(evaluation || null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [evalToArchive, setEvalToArchive] = useState<string | null>(null);
  const [attachingAll, setAttachingAll] = useState(false);
  const [assignAllEvaluations, setAssignAllEvaluations] = useState(true);

  // Human review form state
  const [accuracy, setAccuracy] = useState<number>(4.0);
  const [adherence, setAdherence] = useState<number>(4.0);
  const [tone, setTone] = useState<number>(4.5);
  const [toolEfficiency, setToolEfficiency] = useState<number>(4.0);
  const [hallucination, setHallucination] = useState<boolean>(false);
  const [reasoning, setReasoning] = useState<string>("");
  const [reviewStatus, setReviewStatus] = useState<"completed" | "flagged" | "requires_review">("completed");

  useEffect(() => {
    setIsHumanMode(initialHumanReviewMode);
    setSelectedEval(evaluation || null);
    setSubmitError(null);
  }, [initialHumanReviewMode, isOpen, evaluation]);

  // Use evaluations list passed from thread, or fallback to scorecard evaluations if available
  const allEvaluations =
    evaluations.length > 0
      ? evaluations
      : scorecard?.evaluations && scorecard.evaluations.length > 0
      ? (scorecard.evaluations as EvaluationResponse[])
      : [];

  // Keep selectedEval synchronized when allEvaluations updates (e.g. batch assignment changes)
  useEffect(() => {
    if (selectedEval) {
      const refreshed = allEvaluations.find((e) => e.id === selectedEval.id);
      if (refreshed && refreshed.batch_id !== selectedEval.batch_id) {
        setSelectedEval(refreshed);
      }
    }
  }, [allEvaluations, selectedEval]);

  if (!isOpen) return null;

  const activeEval = selectedEval || (allEvaluations.length === 1 ? allEvaluations[0] : null);

  const getScoreColor = (score?: number | null) => {
    if (score == null) return "text-stone-400 bg-stone-100 dark:bg-stone-800 border-stone-200 dark:border-stone-700";
    if (score >= 4.0) return "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 border-emerald-200 dark:border-emerald-800";
    if (score >= 3.5) return "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800";
    return "text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-800";
  };

  const handleEnterHumanMode = () => {
    if (activeBatches.length === 0 && onRequireActiveBatch) {
      onRequireActiveBatch(() => {
        setIsHumanMode(true);
      });
    } else {
      setIsHumanMode(true);
    }
  };

  const handleArchiveEvaluation = async (evalId: string) => {
    setIsArchiving(true);
    try {
      await evaluationsArchiveEvaluation(evalId);
      toast.info("Human evaluation archived.");
      setSelectedEval(null);
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (err: any) {
      toast.error("Failed to archive evaluation: " + (err?.message || "Unknown error"));
    } finally {
      setIsArchiving(false);
    }
  };

  const handleAttachSingleToBatch = async (evalId: string) => {
    if (activeBatch) {
      setAttachingAll(true);
      try {
        await feedbackBatchesAddEvaluationsToBatch(activeBatch.id, {
          evaluation_ids: [evalId],
        });
        toast.success(`Attached evaluation to batch "${activeBatch.title}".`);
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } catch (err: any) {
        toast.error("Failed to attach evaluation: " + (err?.message || "Unknown error"));
      } finally {
        setAttachingAll(false);
      }
    } else if (onOpenBatchAssignModal && activeEval) {
      onOpenBatchAssignModal([activeEval]);
    } else if (onRequireActiveBatch) {
      onRequireActiveBatch(async (newBatchId) => {
        setAttachingAll(true);
        try {
          await feedbackBatchesAddEvaluationsToBatch(newBatchId, {
            evaluation_ids: [evalId],
          });
          toast.success("Attached evaluation to batch.");
          if (onReviewSubmitted) {
            onReviewSubmitted();
          }
        } catch (err: any) {
          toast.error("Failed to attach evaluation: " + (err?.message || "Unknown error"));
        } finally {
          setAttachingAll(false);
        }
      });
    } else {
      toast.warning("Please select or create an active batch first.");
    }
  };

  const handleAttachAllToBatch = async () => {
    if (allEvaluations.length === 0) {
      toast.info("No evaluations on this thread to attach.");
      return;
    }

    if (activeBatch) {
      setAttachingAll(true);
      try {
        await feedbackBatchesAddEvaluationsToBatch(activeBatch.id, {
          evaluation_ids: allEvaluations.map((e) => e.id),
        });
        toast.success(`Attached ${allEvaluations.length} evaluation(s) to batch "${activeBatch.title}".`);
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } catch (err: any) {
        toast.error("Failed to attach evaluations: " + (err?.message || "Unknown error"));
      } finally {
        setAttachingAll(false);
      }
    } else if (onOpenBatchAssignModal) {
      onOpenBatchAssignModal(allEvaluations);
    } else if (onRequireActiveBatch) {
      onRequireActiveBatch(async (newBatchId) => {
        setAttachingAll(true);
        try {
          await feedbackBatchesAddEvaluationsToBatch(newBatchId, {
            evaluation_ids: allEvaluations.map((e) => e.id),
          });
          toast.success(`Attached ${allEvaluations.length} evaluation(s) to batch.`);
          if (onReviewSubmitted) {
            onReviewSubmitted();
          }
        } catch (err: any) {
          toast.error("Failed to attach evaluations: " + (err?.message || "Unknown error"));
        } finally {
          setAttachingAll(false);
        }
      });
    } else {
      toast.warning("Please select or create an active batch first.");
    }
  };

  const handleSubmitHumanReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const computedScore = hallucination
        ? 1.0
        : Math.round(((accuracy + adherence + tone + toolEfficiency) / 4) * 100) / 100;

      const res = await evaluationsCreateEvaluation({
        thread_id: threadId,
        message_id: messageId || undefined,
        run_id: runId || undefined,
        evaluator_type: "human",
        score: computedScore,
        accuracy_score: accuracy,
        instruction_adherence_score: adherence,
        tone_score: tone,
        tool_efficiency_score: toolEfficiency,
        hallucination_detected: hallucination,
        status: hallucination ? "flagged" : reviewStatus,
        reasoning: reasoning.trim() || undefined,
      });

      const newEvalId = (res as any)?.data?.id || (res as any)?.id;

      if (assignAllEvaluations && activeBatch) {
        const idsToAssign = Array.from(
          new Set([
            ...allEvaluations.map((ev) => ev.id),
            ...(newEvalId ? [newEvalId] : []),
          ])
        );
        if (idsToAssign.length > 0) {
          try {
            await feedbackBatchesAddEvaluationsToBatch(activeBatch.id, {
              evaluation_ids: idsToAssign,
            });
          } catch (batchErr) {
            console.error("Failed to assign evaluations to batch on human review:", batchErr);
          }
        }
      }

      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      setIsHumanMode(false);
      onClose();
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-stone-950/50 backdrop-blur-xs flex justify-end">
      <div className="w-full max-w-lg bg-white dark:bg-stone-900 h-full shadow-2xl border-l border-stone-200 dark:border-stone-800 flex flex-col justify-between">
        {/* Drawer Header */}
        <div className="px-5 py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {isHumanMode ? (
              <button
                type="button"
                onClick={() => setIsHumanMode(false)}
                className="p-1 -ml-1 rounded-lg text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                title="Back to evaluations"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            ) : activeEval && allEvaluations.length > 1 ? (
              <button
                type="button"
                onClick={() => setSelectedEval(null)}
                className="p-1 -ml-1 rounded-lg text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                title="Back to evaluations list"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            ) : (
              <Award className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            )}

            <div>
              <h2 className="text-sm font-bold text-stone-900 dark:text-stone-100">
                {isHumanMode
                  ? "Submit Human Review"
                  : activeEval
                  ? "Evaluation Scorecard"
                  : `Evaluations (${allEvaluations.length})`}
              </h2>
              <span className="text-[10px] text-stone-400 font-mono">
                {isHumanMode
                  ? messageId
                    ? `Turn: ${messageId.slice(0, 8)}...`
                    : `Thread: ${threadId.slice(0, 8)}...`
                  : activeEval
                  ? activeEval.message_id
                    ? `Turn: ${activeEval.message_id.slice(0, 8)}...`
                    : `Thread: ${threadId.slice(0, 8)}...`
                  : `${allEvaluations.length} recorded on this thread`}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {!isHumanMode && (
              <button
                type="button"
                onClick={handleEnterHumanMode}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                title="Add a human review"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add Review</span>
              </button>
            )}

            {activeEval?.evaluator_type === "human" && activeEval.status !== "archived" && (
              <button
                type="button"
                onClick={() => setEvalToArchive(activeEval.id)}
                disabled={isArchiving}
                className="p-1.5 rounded-lg text-stone-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                title="Archive this human review"
              >
                <Archive className="w-4 h-4" />
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 [scrollbar-gutter:stable]">
          {isHumanMode ? (
            <HumanReviewForm
              accuracy={accuracy}
              adherence={adherence}
              tone={tone}
              toolEfficiency={toolEfficiency}
              hallucination={hallucination}
              reasoning={reasoning}
              reviewStatus={reviewStatus}
              submitting={submitting}
              submitError={submitError}
              onAccuracyChange={setAccuracy}
              onAdherenceChange={setAdherence}
              onToneChange={setTone}
              onToolEfficiencyChange={setToolEfficiency}
              onHallucinationChange={setHallucination}
              onReasoningChange={setReasoning}
              onReviewStatusChange={setReviewStatus}
              onCancel={() => setIsHumanMode(false)}
              onSubmit={handleSubmitHumanReview}
              assignAllEvaluations={assignAllEvaluations}
              onAssignAllEvaluationsChange={setAssignAllEvaluations}
              activeBatchTitle={activeBatch?.title}
            />
          ) : activeEval ? (
            <div className="space-y-4">
              {allEvaluations.length > 1 && (
                <button
                  type="button"
                  onClick={() => setSelectedEval(null)}
                  className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 font-medium transition cursor-pointer mb-2"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to all evaluations ({allEvaluations.length})</span>
                </button>
              )}
              <ScorecardSummaryView
                activeEval={activeEval}
                activeBatch={activeBatch}
                allBatches={allBatches}
                onEnterHumanMode={handleEnterHumanMode}
                onArchive={(id) => setEvalToArchive(id)}
                isArchiving={isArchiving}
                onAssignToActiveBatch={async (evalId) => {
                  if (onAssignDirectToActiveBatch) {
                    await onAssignDirectToActiveBatch([evalId]);
                    if (onReviewSubmitted) onReviewSubmitted();
                  } else {
                    await handleAttachSingleToBatch(evalId);
                  }
                }}
                onOpenBatchAssignModal={(evalItem) => {
                  if (onOpenBatchAssignModal) {
                    onOpenBatchAssignModal([evalItem]);
                  }
                }}
                isAssigning={attachingAll}
              />
            </div>
          ) : (
            <div className="space-y-3">
              {allEvaluations.length === 0 ? (
                <div className="p-8 text-center text-xs text-stone-400 space-y-3">
                  <p>No evaluations recorded for this thread yet.</p>
                  <button
                    type="button"
                    onClick={handleEnterHumanMode}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Leave Human Review</span>
                  </button>
                </div>
              ) : (
                <>
                  {/* Attach all evaluations to batch action bar */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50 dark:bg-stone-950/60 border border-stone-200 dark:border-stone-800">
                    <div className="flex items-center gap-2 text-xs min-w-0 pr-2">
                      <Layers className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <div className="truncate">
                        <div className="font-semibold text-stone-900 dark:text-stone-100 truncate">
                          {activeBatch ? activeBatch.title : "No active batch"}
                        </div>
                        <div className="text-[11px] text-stone-500 dark:text-stone-400">
                          {activeBatch
                            ? "Current collecting batch"
                            : "Create or select a batch"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {activeBatch ? (
                        <>
                          <button
                            type="button"
                            onClick={async () => {
                              if (onAssignDirectToActiveBatch) {
                                await onAssignDirectToActiveBatch(allEvaluations.map((e) => e.id));
                                if (onReviewSubmitted) onReviewSubmitted();
                              } else {
                                await handleAttachAllToBatch();
                              }
                            }}
                            disabled={attachingAll}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition disabled:opacity-50 shrink-0 cursor-pointer flex items-center gap-1.5 shadow-2xs"
                            title={`Add all ${allEvaluations.length} evaluations to ${activeBatch.title}`}
                          >
                            <Layers className="w-3.5 h-3.5" />
                            <span>{attachingAll ? "Adding..." : "Add All to Batch"}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (onOpenBatchAssignModal) {
                                onOpenBatchAssignModal(allEvaluations);
                              } else if (onRequireActiveBatch) {
                                onRequireActiveBatch();
                              }
                            }}
                            className="px-2 py-1.5 text-xs font-medium rounded-lg text-stone-600 dark:text-stone-400 hover:bg-stone-200/60 dark:hover:bg-stone-800 border border-stone-200 dark:border-stone-700 transition cursor-pointer"
                            title="Assign all evaluations to a new or different batch"
                          >
                            New / Other...
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (onOpenBatchAssignModal) {
                              onOpenBatchAssignModal(allEvaluations);
                            } else if (onRequireActiveBatch) {
                              onRequireActiveBatch();
                            }
                          }}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition cursor-pointer flex items-center gap-1.5 shadow-2xs"
                        >
                          <Layers className="w-3.5 h-3.5" />
                          <span>Add All to Batch</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {allEvaluations.map((evItem) => (
                    <button
                      key={evItem.id}
                      type="button"
                      onClick={() => setSelectedEval(evItem)}
                      className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/50 hover:bg-white dark:hover:bg-stone-800/80 hover:border-stone-300 dark:hover:border-stone-700 transition cursor-pointer group space-y-2.5 shadow-2xs"
                    >
                      {/* Top Row: Score + Status + Hallucination + Timestamp */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold font-mono border ${getScoreColor(
                              evItem.score
                            )}`}
                          >
                            <Award className="w-3 h-3" />
                            <span>{Number(evItem.score).toFixed(2)}</span>
                          </span>

                          <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                            {evItem.status || "Completed"}
                          </span>

                          {evItem.batch_id && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                              <Layers className="w-2.5 h-2.5" />
                              <span className="max-w-[110px] truncate">
                                {allBatches.find((b) => b.id === evItem.batch_id)?.title || "Batched"}
                              </span>
                            </span>
                          )}

                          {evItem.hallucination_detected && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
                              <ShieldAlert className="w-3 h-3" />
                              <span>Hallucination</span>
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-stone-400 flex items-center gap-1 shrink-0 font-mono">
                            <Clock className="w-3 h-3" />
                            <span>
                              {new Date(evItem.created_at).toLocaleDateString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </span>

                          {evItem.evaluator_type === "human" && evItem.status !== "archived" && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEvalToArchive(evItem.id);
                              }}
                              className="p-1 rounded text-stone-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-stone-200/60 dark:hover:bg-stone-800 transition cursor-pointer"
                              title="Archive this human review"
                            >
                              <Archive className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Middle Row: Evaluator Type + Target Turn */}
                      <div className="flex items-center justify-between text-xs text-stone-600 dark:text-stone-300">
                        <span className="flex items-center gap-1.5 font-medium">
                          {evItem.evaluator_type === "human" ? (
                            <User className="w-3.5 h-3.5 text-stone-400" />
                          ) : (
                            <Zap className="w-3.5 h-3.5 text-stone-400" />
                          )}
                          <span>
                            {evItem.evaluator_type === "human"
                              ? "Human Review"
                              : "Automated Judge"}
                          </span>
                        </span>

                        {evItem.message_id && (
                          <span className="text-[10px] font-mono text-stone-400">
                            Turn: {evItem.message_id.slice(0, 8)}...
                          </span>
                        )}
                      </div>

                      {/* Reasoning Snippet if available */}
                      {evItem.reasoning && (
                        <p className="text-xs text-stone-500 dark:text-stone-400 line-clamp-2 italic">
                          "{evItem.reasoning}"
                        </p>
                      )}

                      {/* Bottom Row: Metric dimensions pills + Click prompt */}
                      <div className="flex items-center justify-between pt-1 border-t border-stone-100 dark:border-stone-800/80 text-[10px] text-stone-500">
                        <div className="flex flex-wrap items-center gap-1.5 font-mono">
                          {evItem.accuracy_score != null && (
                            <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-900 border border-stone-200/80 dark:border-stone-800">
                              Acc: {evItem.accuracy_score.toFixed(1)}
                            </span>
                          )}
                          {evItem.instruction_adherence_score != null && (
                            <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-900 border border-stone-200/80 dark:border-stone-800">
                              Adh: {evItem.instruction_adherence_score.toFixed(1)}
                            </span>
                          )}
                          {evItem.tone_score != null && (
                            <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-900 border border-stone-200/80 dark:border-stone-800">
                              Tone: {evItem.tone_score.toFixed(1)}
                            </span>
                          )}
                          {evItem.tool_efficiency_score != null && (
                            <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-900 border border-stone-200/80 dark:border-stone-800">
                              Tools: {evItem.tool_efficiency_score.toFixed(1)}
                            </span>
                          )}
                        </div>

                        <span className="inline-flex items-center text-[11px] font-medium text-emerald-600 dark:text-emerald-400 group-hover:translate-x-0.5 transition-transform shrink-0 ml-2">
                          <span>Details</span>
                          <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </span>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(evalToArchive)}
        onClose={() => !isArchiving && setEvalToArchive(null)}
        onConfirm={async () => {
          if (!evalToArchive) return;
          await handleArchiveEvaluation(evalToArchive);
          setEvalToArchive(null);
        }}
        title="Archive Evaluation"
        description="Archive this human evaluation? It will be excluded from the scorecard."
        confirmText="Archive"
        loadingText="Archiving..."
        variant="danger"
        isLoading={isArchiving}
      />
    </div>
  );
}
